import type { ApiException } from '@/common/error';
import { mapTemporaryToDto } from '@/store/contracts/contracts.mappers';

import type {
  CreateInvitation,
  InvitationDto,
  InviteCode,
  InviteCodeDto,
  JoinedVessel,
  JoinInviteCodeDto,
  SeatAvailability,
  SeatAvailabilityDto,
} from './invites.types';

export const mapInviteToDto = ({
  personId,
  role,
  serviceId,
  serviceType,
  roleName,
  vesselName,
  name,
  event,
  message,
  temporary,
}: CreateInvitation): InvitationDto => ({
  person_id: personId,
  role,
  service_id: serviceId,
  service_type: serviceType,
  role_name: roleName,
  service_name: vesselName,
  name,
  event,
  message,
  temporary: mapTemporaryToDto(temporary),
});

export const mapInviteCode = ({
  id,
  code,
  service_id,
  role,
  expires_at,
  seat_available,
  seat_auto_charge,
}: InviteCodeDto): InviteCode => ({
  id,
  code,
  serviceId: service_id,
  role,
  expiresAt: expires_at,
  seatAvailable: seat_available,
  seatAutoCharge: seat_auto_charge,
});

export const mapSeatAvailability = ({
  pool,
  available,
  auto_charge,
}: SeatAvailabilityDto): SeatAvailability => ({ pool, available, autoCharge: auto_charge });

/**
 * What the invite form says about the seat the invitee takes, or nothing when a free one is
 * waiting (or the plan pays for none). A temporary hire's bought seat goes off the plan again when
 * the contract ends.
 */
export const seatAvailabilityWarning = (
  seat: SeatAvailability | undefined,
  temporary = false,
): string | undefined => {
  if (!seat?.pool || seat.available) return undefined;

  if (!seat.autoCharge) {
    return `There's no free ${seat.pool} seat and this plan can't add one, so they'll join with Harbor access until a seat is freed.`;
  }

  return `There's no free ${seat.pool} seat. When they join, one more is added to the plan and charged to its payer${temporary ? ', then taken off again when the contract ends' : ''}. Free a seat first to avoid the charge.`;
};

export const mapJoinedVessel = ({
  service_id,
  service_name,
  role,
}: JoinInviteCodeDto): JoinedVessel => ({
  serviceId: service_id,
  serviceName: service_name || undefined,
  role,
});

/**
 * What to say when a code does not let somebody in. The people service answers a mistyped,
 * taken, withdrawn or lapsed code with the same 404 on purpose, so the copy does not tell them
 * apart either.
 */
export const inviteCodeErrorMessage = (error: unknown): string => {
  const { domain, type } = (error as Partial<ApiException> | null) ?? {};
  if (domain === 'INVITES' && type === 'NOT_FOUND')
    return 'That code is wrong or has expired. Please ask for a new one.';
  if (type === 'ALREADY_ON_VESSEL')
    return 'You’re already on this vessel. Please open it from your dashboard.';
  return 'Couldn’t join the vessel. Please try again.';
};
